export const SEARCH_INDEX = "sitzungspaket";

export interface DateRangeState {
  from?: string;
  to?: string;
}

interface SearchUiState {
  query?: string;
  refinementList?: Record<string, string[]>;
  sortBy?: string;
  page?: number;
  dateRange?: DateRangeState;
  searchMode?: "documents" | "ask";
}

type UiState = Record<string, SearchUiState | undefined>;
type RouteState = Record<string, unknown>;

export interface SearchHit {
  objectID?: string;
  id?: string | number;
  hash?: string;
  title?: string | { value?: string };
  Kommune?: string;
  Gremium?: string;
  start?: number | null;
  meeting_id?: string;
  _group_found?: number;
  [key: string]: unknown;
}

export interface GroupedSearchHit extends SearchHit {
  _duplicateCount: number;
  _duplicateScope: "index" | "page";
  _duplicateContexts: Array<{
    kommune: string;
    gremium: string;
    start: number | null;
    meetingId: string;
  }>;
}

const SORT_TO_ROUTE: Record<string, string> = {
  [`${SEARCH_INDEX}/sort/start:desc`]: "neueste",
  [`${SEARCH_INDEX}/sort/start:asc`]: "aelteste",
};

const ROUTE_TO_SORT: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_TO_ROUTE).map(([index, route]) => [route, index])
);

function firstString(value: unknown): string | undefined {
  if (Array.isArray(value)) return firstString(value[0]);
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function stringList(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values
    .flatMap((entry) => (typeof entry === "string" ? entry.split(",") : []))
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

function startOfUtcDay(value: string): number {
  return Math.floor(new Date(`${value}T00:00:00Z`).getTime() / 1000);
}

export function buildDateFilter(
  range: DateRangeState | undefined
): string | undefined {
  const from = isIsoDate(range?.from) ? range.from : undefined;
  const to = isIsoDate(range?.to) ? range.to : undefined;
  if (!from && !to) return undefined;

  const fromTimestamp = from ? startOfUtcDay(from) : undefined;
  const toTimestamp = to ? startOfUtcDay(to) + 86_399 : undefined;
  if (fromTimestamp !== undefined && toTimestamp !== undefined) {
    return `start:[${fromTimestamp}..${toTimestamp}]`;
  }
  return fromTimestamp !== undefined
    ? `start:>=${fromTimestamp}`
    : `start:<=${toTimestamp}`;
}

function isoDateFromTimestamp(timestamp: number): string | undefined {
  if (!Number.isFinite(timestamp)) return undefined;
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

export function dateRangeFromFilter(
  filter: unknown
): DateRangeState | undefined {
  if (typeof filter !== "string") return undefined;
  const between = filter.match(/^start:\[(\d+)\.\.(\d+)\]$/);
  if (between) {
    return {
      from: isoDateFromTimestamp(Number(between[1])),
      to: isoDateFromTimestamp(Number(between[2]) - 86_399),
    };
  }
  const from = filter.match(/^start:>=(\d+)$/);
  if (from) return { from: isoDateFromTimestamp(Number(from[1])) };
  const to = filter.match(/^start:<=(\d+)$/);
  if (to) return { to: isoDateFromTimestamp(Number(to[1]) - 86_399) };
  return undefined;
}

function addList(
  target: Record<string, string[]>,
  attribute: string,
  value: unknown
): void {
  const values = stringList(value);
  if (values.length) target[attribute] = values;
}

/** A compact, readable URL representation of the InstantSearch UI state. */
export function searchStateToRoute(uiState: UiState): RouteState {
  const state = uiState[SEARCH_INDEX] ?? {};
  const route: RouteState = {};
  if (state.query?.trim()) route.q = state.query.trim();
  if (state.refinementList?.Kommune?.length) {
    route.kommune = state.refinementList.Kommune;
  }
  if (state.refinementList?.Gremium?.length) {
    route.gremium = state.refinementList.Gremium;
  }
  if (state.refinementList?.themengruppe?.length) {
    route.thema = state.refinementList.themengruppe;
  }
  const sort = state.sortBy ? SORT_TO_ROUTE[state.sortBy] : undefined;
  if (sort) route.sort = sort;
  if (state.page && state.page > 1) route.seite = state.page;
  if (isIsoDate(state.dateRange?.from)) route.von = state.dateRange.from;
  if (isIsoDate(state.dateRange?.to)) route.bis = state.dateRange.to;
  if (state.searchMode === "ask") route.modus = "frage";
  return route;
}

/** Accepts the new URL names and the older direct facet links. */
export function searchRouteToState(route: RouteState): UiState {
  const state: SearchUiState = {};
  const query = firstString(route.q ?? route.query);
  if (query) state.query = query;

  const refinementList: Record<string, string[]> = {};
  addList(refinementList, "Kommune", route.kommune ?? route.Kommune);
  addList(refinementList, "Gremium", route.gremium ?? route.Gremium);
  addList(refinementList, "themengruppe", route.thema ?? route.themengruppe);
  if (Object.keys(refinementList).length) state.refinementList = refinementList;

  const sort = firstString(route.sort);
  if (sort && ROUTE_TO_SORT[sort]) state.sortBy = ROUTE_TO_SORT[sort];
  const rawPage = Array.isArray(route.seite) ? route.seite[0] : route.seite;
  const page =
    typeof rawPage === "number" ? rawPage : Number(firstString(rawPage));
  if (Number.isInteger(page) && page > 1) state.page = page;

  const from = firstString(route.von);
  const to = firstString(route.bis);
  if (isIsoDate(from) || isIsoDate(to)) {
    state.dateRange = {
      ...(isIsoDate(from) ? { from } : {}),
      ...(isIsoDate(to) ? { to } : {}),
    };
  }

  if (["frage", "ask"].includes(firstString(route.modus) ?? "")) {
    state.searchMode = "ask";
  }
  return { [SEARCH_INDEX]: state };
}

function hitContext(item: SearchHit) {
  return {
    kommune: item.Kommune || "",
    gremium: item.Gremium || "",
    start: typeof item.start === "number" ? item.start : null,
    meetingId: item.meeting_id || "",
  };
}

/**
 * Typesense's grouped response is already flattened by the InstantSearch adapter.
 * `_group_found` retains the full index-wide count. Without schema support, the
 * same shape is produced by a transparent per-page fallback.
 */
export function groupSearchHits(
  items: SearchHit[],
  indexWide: boolean
): GroupedSearchHit[] {
  if (indexWide) {
    return items.map((item) => ({
      ...item,
      _duplicateCount: Math.max(1, Number(item._group_found) || 1),
      _duplicateScope: "index",
      _duplicateContexts: [hitContext(item)],
    }));
  }

  const grouped = new Map<string, GroupedSearchHit>();
  items.forEach((item, index) => {
    const key = item.hash || `id:${item.objectID || item.id || index}`;
    const context = hitContext(item);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        ...item,
        _duplicateCount: 1,
        _duplicateScope: "page",
        _duplicateContexts: [context],
      });
      return;
    }
    existing._duplicateCount += 1;
    if (
      !existing._duplicateContexts.some(
        (candidate) => JSON.stringify(candidate) === JSON.stringify(context)
      )
    ) {
      existing._duplicateContexts.push(context);
    }
  });
  return Array.from(grouped.values());
}

function formatNumericDate(value: unknown): string {
  const date =
    typeof value === "number"
      ? new Date(value * 1000)
      : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  return [
    String(date.getUTCDate()).padStart(2, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    date.getUTCFullYear(),
  ].join(".");
}

const GENERIC_TITLE =
  /^(?:anlage(?:[\s_-]*\d+[a-z]?)?|anhang(?:[\s_-]*\d+[a-z]?)?|begründung|antrag|bekanntmachung|öffentliche bekanntmachung|beschluss(?:vorlage)?|dokument|einladung|mitteilung(?:svorlage)?|niederschrift|protokoll|sitzungsvorlage|tagesordnung|vorlage)$/i;

/** Adds useful context only when the source title itself says almost nothing. */
export function contextualDocumentTitle(doc: SearchHit): string {
  const rawTitle = typeof doc.title === "string" ? doc.title : doc.title?.value;
  const title = rawTitle?.replace(/\s+/g, " ").trim() || "Dokument";
  if (!GENERIC_TITLE.test(title)) return title;

  const context = (doc.Gremium || doc.Kommune || "").trim();
  const date = doc.start ? formatNumericDate(doc.start) : "";
  const details = [context, date].filter(Boolean).join(" · ");
  return details ? `${title} – ${details}` : title;
}

/** Obergrenze von /api/vorgang (per_page in server.ts). */
export const VORGANG_LIMIT = 50;

/** Mehr Gremien hat eine echte, über den Betreff gefundene Beratungsfolge nicht. */
const MAX_BETREFF_GREMIEN = 4;

/**
 * Ob sich eine Vorgangs-Gruppierung als Beratungsfolge anzeigen lässt.
 *
 * „az:…“-Gruppen hängen an einem Aktenzeichen und gelten als belastbar.
 * „be:…“-Gruppen bildet der Parser über einen ähnlichen Betreff. Bei
 * Standardtiteln wie „Bekanntmachung“ oder „Niederschrift“ landen so beliebig
 * viele unzusammenhängende Dokumente aus allen Gremien in einer Gruppe. Solche
 * Gruppen verwerfen wir: am Titel schon vor dem Laden, nach dem Laden außerdem,
 * wenn die Gruppe über zu viele Gremien streut. Die Obergrenze von /api/vorgang
 * taugt dafür nicht: Dieselbe Datei liegt je Sitzung einmal im Index, sodass
 * auch echte Beratungsfolgen sie erreichen.
 */
export function isTrustedVorgang(
  vorgangId: unknown,
  title: SearchHit["title"] | null | undefined,
  docs?: Array<{ Gremium?: string | null }>
): boolean {
  if (typeof vorgangId !== "string" || !vorgangId) return false;
  if (!vorgangId.startsWith("be:")) return true;
  const rawTitle = typeof title === "string" ? title : title?.value;
  const normalized = rawTitle?.replace(/\s+/g, " ").trim() || "";
  if (!normalized || GENERIC_TITLE.test(normalized)) return false;
  if (!docs) return true;
  const gremien = new Set(docs.map((d) => d.Gremium).filter(Boolean));
  return gremien.size <= MAX_BETREFF_GREMIEN;
}
